import { useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useProcurement } from "@/context/ProcurementContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Settings as SettingsIcon, 
  User, 
  Bell, 
  Shield, 
  Database,
  Palette,
  Globe,
  Save,
  RefreshCw,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

const Settings = () => {
  const { actions } = useProcurement();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [settings, setSettings] = useState({
    // General Settings
    companyName: "KKG Electricals Management System",
    timezone: "UTC+5:30",
    dateFormat: "DD/MM/YYYY",
    currency: "INR",
    
    // User Preferences
    theme: "system",
    language: "en",
    itemsPerPage: 10,
    autoSave: true,
    
    // Notifications
    emailNotifications: true,
    pushNotifications: true,
    soundNotifications: false,
    lowStockAlerts: true,
    poApprovalAlerts: true,
    invoiceReminders: true,
    
    // System Settings
    dataRetention: 365,
    backupFrequency: "daily",
    auditLogging: true,
    twoFactorAuth: false,
    
    // Display Settings
    dashboardRefreshInterval: 30,
    defaultView: "overview",
    showTutorials: true
  });

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRefreshMockData = async () => {
    setIsRefreshing(true);
    try {
      await actions.refreshMockData();
      toast.success("Mock data refreshed successfully!");
    } catch (error) {
      console.error("Failed to refresh mock data:", error);
      toast.error("Failed to refresh mock data");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearAllData = async () => {
    if (window.confirm("Are you sure you want to clear all data? This action cannot be undone.")) {
      try {
        await actions.clearAllData();
        toast.success("All data cleared successfully!");
      } catch (error) {
        console.error("Failed to clear data:", error);
        toast.error("Failed to clear data");
      }
    }
  };

  const handleSave = () => {
    // In a real app, this would save to backend
    localStorage.setItem('appSettings', JSON.stringify(settings));
    toast.success("Settings saved successfully");
  };

  const handleReset = () => {
    // Reset to default settings
    const defaultSettings = {
      companyName: "KKG Electricals Management System",
      timezone: "UTC+5:30",
      dateFormat: "DD/MM/YYYY",
      currency: "INR",
      theme: "system",
      language: "en",
      itemsPerPage: 10,
      autoSave: true,
      emailNotifications: true,
      pushNotifications: true,
      soundNotifications: false,
      lowStockAlerts: true,
      poApprovalAlerts: true,
      invoiceReminders: true,
      dataRetention: 365,
      backupFrequency: "daily",
      auditLogging: true,
      twoFactorAuth: false,
      dashboardRefreshInterval: 30,
      defaultView: "overview",
      showTutorials: true
    };
    setSettings(defaultSettings);
    toast.success("Settings reset to defaults");
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">System Settings</h1>
            <p className="text-muted-foreground">Configure your application preferences and system options</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset}>
              Reset to Defaults
            </Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </Button>
          </div>
        </div>

        <Tabs defaultValue="general" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <SettingsIcon className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="user" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              User
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Security
            </TabsTrigger>
            <TabsTrigger value="system" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              System
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  General Settings
                </CardTitle>
                <CardDescription>
                  Basic application configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company Name</Label>
                    <Input
                      id="companyName"
                      value={settings.companyName}
                      onChange={(e) => handleSettingChange('companyName', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Input
                      id="timezone"
                      value={settings.timezone}
                      onChange={(e) => handleSettingChange('timezone', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dateFormat">Date Format</Label>
                    <Input
                      id="dateFormat"
                      value={settings.dateFormat}
                      onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <Input
                      id="currency"
                      value={settings.currency}
                      onChange={(e) => handleSettingChange('currency', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Settings */}
          <TabsContent value="user" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  User Preferences
                </CardTitle>
                <CardDescription>
                  Personalize your experience
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="theme">Theme</Label>
                    <Input
                      id="theme"
                      value={settings.theme}
                      onChange={(e) => handleSettingChange('theme', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="language">Language</Label>
                    <Input
                      id="language"
                      value={settings.language}
                      onChange={(e) => handleSettingChange('language', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="itemsPerPage">Items Per Page</Label>
                    <Input
                      id="itemsPerPage"
                      type="number"
                      value={settings.itemsPerPage}
                      onChange={(e) => handleSettingChange('itemsPerPage', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dashboardRefreshInterval">Dashboard Refresh (seconds)</Label>
                    <Input
                      id="dashboardRefreshInterval"
                      type="number"
                      value={settings.dashboardRefreshInterval}
                      onChange={(e) => handleSettingChange('dashboardRefreshInterval', parseInt(e.target.value))}
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="autoSave"
                    checked={settings.autoSave}
                    onCheckedChange={(checked) => handleSettingChange('autoSave', checked)}
                  />
                  <Label htmlFor="autoSave">Auto-save changes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="showTutorials"
                    checked={settings.showTutorials}
                    onCheckedChange={(checked) => handleSettingChange('showTutorials', checked)}
                  />
                  <Label htmlFor="showTutorials">Show tutorials and tips</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications */}
          <TabsContent value="notifications" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notification Settings
                </CardTitle>
                <CardDescription>
                  Configure how you receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="emailNotifications">Email Notifications</Label>
                      <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                    </div>
                    <Switch
                      id="emailNotifications"
                      checked={settings.emailNotifications}
                      onCheckedChange={(checked) => handleSettingChange('emailNotifications', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="pushNotifications">Push Notifications</Label>
                      <p className="text-sm text-muted-foreground">Receive browser push notifications</p>
                    </div>
                    <Switch
                      id="pushNotifications"
                      checked={settings.pushNotifications}
                      onCheckedChange={(checked) => handleSettingChange('pushNotifications', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="soundNotifications">Sound Notifications</Label>
                      <p className="text-sm text-muted-foreground">Play sound for notifications</p>
                    </div>
                    <Switch
                      id="soundNotifications"
                      checked={settings.soundNotifications}
                      onCheckedChange={(checked) => handleSettingChange('soundNotifications', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="lowStockAlerts">Low Stock Alerts</Label>
                      <p className="text-sm text-muted-foreground">Get notified when stock is low</p>
                    </div>
                    <Switch
                      id="lowStockAlerts"
                      checked={settings.lowStockAlerts}
                      onCheckedChange={(checked) => handleSettingChange('lowStockAlerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="poApprovalAlerts">PO Approval Alerts</Label>
                      <p className="text-sm text-muted-foreground">Get notified about pending approvals</p>
                    </div>
                    <Switch
                      id="poApprovalAlerts"
                      checked={settings.poApprovalAlerts}
                      onCheckedChange={(checked) => handleSettingChange('poApprovalAlerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="invoiceReminders">Invoice Reminders</Label>
                      <p className="text-sm text-muted-foreground">Get reminded about overdue invoices</p>
                    </div>
                    <Switch
                      id="invoiceReminders"
                      checked={settings.invoiceReminders}
                      onCheckedChange={(checked) => handleSettingChange('invoiceReminders', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security */}
          <TabsContent value="security" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Security Settings
                </CardTitle>
                <CardDescription>
                  Manage security and access controls
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="twoFactorAuth">Two-Factor Authentication</Label>
                      <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
                    </div>
                    <Switch
                      id="twoFactorAuth"
                      checked={settings.twoFactorAuth}
                      onCheckedChange={(checked) => handleSettingChange('twoFactorAuth', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="auditLogging">Audit Logging</Label>
                      <p className="text-sm text-muted-foreground">Log all user actions for security</p>
                    </div>
                    <Switch
                      id="auditLogging"
                      checked={settings.auditLogging}
                      onCheckedChange={(checked) => handleSettingChange('auditLogging', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System */}
          <TabsContent value="system" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  System Settings
                </CardTitle>
                <CardDescription>
                  System configuration and maintenance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dataRetention">Data Retention (days)</Label>
                    <Input
                      id="dataRetention"
                      type="number"
                      value={settings.dataRetention}
                      onChange={(e) => handleSettingChange('dataRetention', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="backupFrequency">Backup Frequency</Label>
                    <Input
                      id="backupFrequency"
                      value={settings.backupFrequency}
                      onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                    />
                  </div>
                </div>

                {/* Data Management */}
                <div className="space-y-4 pt-6 border-t">
                  <h3 className="text-lg font-semibold">Data Management</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <RefreshCw className="h-4 w-4" />
                          Refresh Mock Data
                        </CardTitle>
                        <CardDescription>
                          Reload fresh mock data with latest updates
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          onClick={handleRefreshMockData}
                          disabled={isRefreshing}
                          className="w-full"
                          variant="outline"
                        >
                          {isRefreshing ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Refreshing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Refresh Data
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Trash2 className="h-4 w-4 text-red-600" />
                          Clear All Data
                        </CardTitle>
                        <CardDescription>
                          Remove all stored data and reset to defaults
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          onClick={handleClearAllData}
                          className="w-full"
                          variant="destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Clear All Data
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default Settings;
